// ============================================
// pages/Login.jsx - Scriptus Pharma Partner
// Design Glassmorphism consistente com Scriptus RxIA
// Com tratamento completo de cenários de conta
// ✅ Compatível com AuthContext.jsx e firebase.js
// ✅ Compatível com estrutura PT-BR (statusAssinatura) e legada (ativo)
// ✅ CORRIGIDO: Helpers centralizados importados do AuthContext
// ============================================

import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';

// ✅ CORRIGIDO: Importar helpers centralizados do AuthContext
import { 
  useAuth,
  isContaArquivada,
  isContaBloqueada,
  isContaAtiva
} from '../contexts/AuthContext';

import { auth, db } from '../config/firebase';
import { doc, getDoc } from 'firebase/firestore';
import { signOut as firebaseSignOut } from 'firebase/auth';

export function Login() {
  const navigate = useNavigate();
  const { signIn, loading, resetPassword } = useAuth();
  
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [erro, setErro] = useState('');
  const [mensagemTipo, setMensagemTipo] = useState('');
  const [resetSent, setResetSent] = useState(false);
  const [scrollY, setScrollY] = useState(0);
  const [isProcessing, setIsProcessing] = useState(false);

  useEffect(() => {
    const handleScroll = () => setScrollY(window.scrollY);
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const exibirMensagem = (tipo, mensagem) => {
    setMensagemTipo(tipo);
    setErro(mensagem);
  };

  // ✅ REMOVIDO: Helpers locais duplicados
  // Agora usando helpers centralizados importados do AuthContext:
  // - isContaArquivada
  // - isContaBloqueada
  // - isContaAtiva

  const handleLogin = async (e) => {
    e.preventDefault();
    setErro('');
    setMensagemTipo('');
    setResetSent(false);

    if (!email || !password) {
      exibirMensagem('aviso', 'Por favor, preencha seu email e senha para continuar.');
      return;
    }

    setIsProcessing(true);

    try {
      const result = await signIn(email, password);

      if (result.success) {
        // ✅ Buscar dados da farmácia para verificar status da conta
        const currentUser = auth.currentUser;
        
        if (currentUser?.uid) {
          const docRef = doc(db, 'pharmacies', currentUser.uid);
          const docSnap = await getDoc(docRef);

          if (docSnap.exists()) {
            const pharmacy = docSnap.data();

            // ✅ Cenário 1: Conta arquivada (excluída permanentemente)
            if (isContaArquivada(pharmacy)) {
              // Importante: fazer logout antes de mostrar mensagem
              await firebaseSignOut(auth);
              exibirMensagem(
                'bloqueado',
                'Esta conta foi encerrada permanentemente a pedido do usuário. Para voltar a usar a Scriptus Pharma, você pode criar uma nova conta clicando em "Criar Conta" acima. Seus dados anteriores não poderão ser recuperados.'
              );
              setIsProcessing(false);
              return;
            }

            // ✅ Cenário 2: Conta bloqueada/desativada
            if (isContaBloqueada(pharmacy)) {
              // Importante: fazer logout antes de mostrar mensagem
              await firebaseSignOut(auth);
              exibirMensagem(
                'bloqueado',
                'Sua conta está temporariamente desativada. Entre em contato com o suporte através do email suporte@scriptusrxia.com.br para mais informações.'
              );
              setIsProcessing(false);
              return;
            }

            // ✅ Cenário 3: Conta ativa - vai para dashboard
            if (isContaAtiva(pharmacy)) {
              console.log('✅ Login bem-sucedido - redirecionando para dashboard');
              console.log('   Plano:', pharmacy.plano || 'basic');
              console.log('   Status:', pharmacy.statusAssinatura || 'N/A');
              toast.success('Login realizado com sucesso!');
              navigate('/dashboard');
            } else {
              // Cenário improvável: não ativa mas também não bloqueada
              // Deixa ir para o dashboard e deixa o PrivateRoute tratar
              console.warn('⚠️ Status de conta indefinido, redirecionando para dashboard');
              toast.success('Login realizado com sucesso!');
              navigate('/dashboard');
            }
          } else {
            // ✅ Cenário 4: Documento não existe no Firestore (caso raro)
            // Nota: O AuthContext já trata isso, mas mantemos como fallback
            await firebaseSignOut(auth);
            exibirMensagem(
              'erro',
              'Não encontramos seus dados cadastrais. Entre em contato com o suporte através do email suporte@scriptusrxia.com.br para resolvermos esta situação.'
            );
          }
        } else {
          // Fallback: se não tiver currentUser mas login foi sucesso
          toast.success('Login realizado com sucesso!');
          navigate('/dashboard');
        }
      } else {
        // Sistema de mensagens contextuais por tipo de erro
        const errorCode = result.code || '';
        
        if (errorCode === 'auth/user-not-found') {
          exibirMensagem('info', 'Este email ainda não está cadastrado. Clique em "Criar Conta" para começar gratuitamente!');
        } else if (errorCode === 'auth/wrong-password') {
          exibirMensagem('erro', 'A senha está incorreta. Verifique se digitou corretamente ou clique em "Esqueci minha senha" abaixo.');
        } else if (errorCode === 'auth/invalid-email') {
          exibirMensagem('erro', 'O formato do email não está correto. Por favor, digite um email válido.');
        } else if (errorCode === 'auth/too-many-requests') {
          exibirMensagem('bloqueado', 'Muitas tentativas de login. Por segurança, aguarde 15 minutos e tente novamente.');
        } else if (errorCode === 'auth/network-request-failed') {
          exibirMensagem('aviso', 'Não conseguimos conectar ao servidor. Verifique sua conexão com a internet.');
        } else if (errorCode === 'auth/user-disabled') {
          exibirMensagem('bloqueado', 'Esta conta foi desativada. Entre em contato com o suporte através do email suporte@scriptusrxia.com.br para mais informações.');
        } else if (errorCode === 'auth/invalid-credential') {
          exibirMensagem('erro', 'Email ou senha incorretos. Verifique seus dados e tente novamente.');
        } else if (errorCode === 'auth/nao-e-farmacia') {
          exibirMensagem('erro', 'Esta conta não é uma farmácia cadastrada. Verifique se você está usando o portal correto ou crie uma nova conta.');
        } else {
          exibirMensagem('erro', result.error || 'Ocorreu um erro ao fazer login. Tente novamente.');
        }
      }
    } catch (error) {
      console.error('Erro no login:', error);
      exibirMensagem('erro', 'Ocorreu um erro inesperado ao fazer login. Tente novamente em alguns instantes.');
    }

    setIsProcessing(false);
  };

  const handleEsqueciSenha = async () => {
    if (!email) {
      exibirMensagem('aviso', 'Digite seu email no campo acima primeiro para enviarmos as instruções de recuperação.');
      return;
    }

    try {
      if (resetPassword) {
        await resetPassword(email);
      }
      setResetSent(true);
      setErro('');
      setMensagemTipo('');
    } catch (error) {
      const errorCode = error.code || '';
      
      if (errorCode === 'auth/user-not-found') {
        exibirMensagem('info', 'Este email não está cadastrado. Verifique se digitou corretamente ou crie uma nova conta.');
      } else if (errorCode === 'auth/invalid-email') {
        exibirMensagem('erro', 'O formato do email não está correto. Por favor, digite um email válido.');
      } else if (errorCode === 'auth/too-many-requests') {
        exibirMensagem('aviso', 'Você solicitou a recuperação de senha muitas vezes. Aguarde alguns minutos.');
      } else {
        exibirMensagem('erro', 'Não foi possível enviar o email de recuperação. Tente novamente.');
      }
    }
  };

  const IconeMensagem = ({ tipo }) => {
    const icones = {
      erro: (
        <svg className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
        </svg>
      ),
      aviso: (
        <svg className="w-5 h-5 text-amber-500 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
      ),
      info: (
        <svg className="w-5 h-5 text-blue-500 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
      ),
      bloqueado: (
        <svg className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
        </svg>
      ),
      sucesso: (
        <svg className="w-5 h-5 text-emerald-500 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
      )
    };
    return icones[tipo] || icones.erro;
  };

  const getEstiloMensagem = (tipo) => {
    const estilos = {
      erro: 'bg-red-50/80 border-red-200/50 text-red-600',
      aviso: 'bg-amber-50/80 border-amber-200/50 text-amber-700',
      info: 'bg-blue-50/80 border-blue-200/50 text-blue-700',
      bloqueado: 'bg-red-100/80 border-red-300/50 text-red-700',
      sucesso: 'bg-emerald-50/80 border-emerald-200/50 text-emerald-600'
    };
    return estilos[tipo] || estilos.erro;
  };

  // Estado de loading combinado
  const isLoading = loading || isProcessing;

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-emerald-50/30 relative">
      {/* Background Pattern */}
      <div className="fixed inset-0 z-0 pointer-events-none opacity-10">
        <div className="absolute inset-0" style={{
          backgroundImage: `
            radial-gradient(circle at 25% 25%, rgb(16 185 129 / 0.08) 0%, transparent 50%),
            radial-gradient(circle at 75% 75%, rgb(20 184 166 / 0.05) 0%, transparent 50%)
          `
        }}></div>
      </div>

      {/* Header */}
      <div className={`fixed top-0 left-0 right-0 z-30 transition-all duration-300 ${
        scrollY > 10 ? 'glass-header-scrolled' : 'glass-header'
      }`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3 sm:py-4">
          <div className="flex items-center justify-between">
            <a 
              href="https://scriptusrxia.com.br/" 
              className="group transition-all duration-300 hover:opacity-80"
            >
              <img 
                src="/logo.PNG"
                alt="Scriptus RxIA" 
                className="h-4 sm:h-5 w-auto"
              />
            </a>
            
            <nav className="flex items-center">
              <Link to="/register" className="button-glass text-xs sm:text-sm text-gray-600 hover:text-gray-800">
                Criar Conta
              </Link>
            </nav>
          </div>
        </div>
      </div>

      <div className="flex items-center justify-center min-h-screen pt-20 sm:pt-24 px-4 sm:px-6 relative z-10">
        <div className="w-full max-w-6xl">
          <div className="grid lg:grid-cols-2 gap-8 sm:gap-12 items-center">
            
            {/* Painel de Boas-vindas */}
            <div className="text-center lg:text-left">
              <div className="glass-badge inline-flex items-center gap-2 px-4 py-2 mb-6">
                <div className="w-2 h-2 bg-emerald-500 rounded-full animate-pulse"></div>
                <span className="sf-pro-text font-medium text-gray-700 text-sm">Portal da Farmácia</span>
              </div>
              
              <h1 className="text-3xl sm:text-4xl lg:text-5xl sf-pro-display font-medium leading-tight mb-6 tracking-tight">
                <span className="text-gray-900">Entre na sua</span>
                <br />
                <span className="text-emerald-600">
                  Scriptus<span className="text-gray-600"> Pharma</span>
                </span>
              </h1>
              
              <p className="text-lg sm:text-xl text-gray-600 leading-relaxed mb-8 max-w-lg sf-pro-text font-normal">
                Valide receitas digitais, gere XMLs SNGPC automaticamente e conecte-se ao maior ecossistema de prescrições do Brasil.
              </p>

              <div className="space-y-4 mb-8">
                <div className="flex items-center gap-3">
                  <div className="icon-container p-2 rounded-xl">
                    <svg className="w-4 h-4 text-emerald-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  </div>
                  <span className="text-gray-700 sf-pro-text font-medium">Validação ICP-Brasil automática</span>
                </div>
                
                <div className="flex items-center gap-3">
                  <div className="icon-container p-2 rounded-xl">
                    <svg className="w-4 h-4 text-teal-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                  </div>
                  <span className="text-gray-700 sf-pro-text font-medium">Geração de XML SNGPC em segundos</span>
                </div>

                <div className="flex items-center gap-3">
                  <div className="icon-container p-2 rounded-xl bg-emerald-100/80 border-emerald-200/50">
                    <span className="text-emerald-600 text-xs font-bold sf-pro-text">₿</span>
                  </div>
                  <span className="text-gray-700 sf-pro-text font-medium">Gratuito para até 50 dispensações/mês</span>
                </div>
              </div>

              <div className="glass-card p-4 rounded-2xl shadow-sm">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="flex -space-x-2">
                      {[...Array(4)].map((_, i) => (
                        <div key={i} className="w-8 h-8 bg-gradient-to-br from-emerald-500 to-teal-600 rounded-full border-2 border-white flex items-center justify-center text-white text-xs sf-pro-text font-bold">
                          {String.fromCharCode(65 + i)}
                        </div>
                      ))}
                    </div>
                    <div>
                      <p className="text-sm sf-pro-text font-semibold text-gray-800">+500 farmácias</p>
                      <p className="text-xs sf-pro-text text-gray-600">já utilizam a plataforma</p>
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="flex items-center gap-1">
                      {[...Array(5)].map((_, i) => (
                        <svg key={i} className="w-4 h-4 text-yellow-400 fill-current" viewBox="0 0 20 20">
                          <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                        </svg>
                      ))}
                    </div>
                    <p className="text-xs sf-pro-text text-gray-600">Avaliação 5.0</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Formulário de Login */}
            <div className="relative">
              <div className="glass-card-elevated rounded-2xl p-6 sm:p-8 shadow-xl">
                <div className="text-center mb-8">
                  <div className="icon-container-pharma w-16 h-16 rounded-xl flex items-center justify-center mx-auto mb-4 shadow-lg">
                    <svg className="w-8 h-8 text-emerald-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                    </svg>
                  </div>
                  <h2 className="text-2xl sf-pro-display font-semibold text-gray-900 mb-2 tracking-tight">Entrar na Scriptus Pharma</h2>
                  <p className="text-gray-600 sf-pro-text font-medium">Acesse sua conta e comece a dispensar com inteligência</p>
                </div>

                <form onSubmit={handleLogin} className="space-y-6">
                  {/* Sistema de mensagens */}
                  {erro && mensagemTipo && (
                    <div className={`glass-alert ${getEstiloMensagem(mensagemTipo)} rounded-xl border`}>
                      <div className="flex items-start gap-3">
                        <IconeMensagem tipo={mensagemTipo} />
                        <div className="flex-1 min-w-0">
                          <p className="sf-pro-text text-sm leading-relaxed">{erro}</p>
                        </div>
                      </div>
                    </div>
                  )}

                  {resetSent && (
                    <div className="glass-alert bg-emerald-50/80 border-emerald-200/50 text-emerald-600 rounded-xl border">
                      <div className="flex items-start gap-3">
                        <svg className="w-5 h-5 text-emerald-500 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                        <div className="flex-1 min-w-0">
                          <p className="sf-pro-text text-sm font-medium mb-1">Email de recuperação enviado!</p>
                          <p className="sf-pro-text text-sm">Verifique sua caixa de entrada e siga as instruções. Não esqueça de verificar a pasta de spam.</p>
                        </div>
                      </div>
                    </div>
                  )}

                  <div>
                    <label className="block text-sm sf-pro-text font-semibold text-gray-700 mb-2">E-mail</label>
                    <div className="relative">
                      <svg className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 12a4 4 0 10-8 0 4 4 0 008 0zm0 0v1.5a2.5 2.5 0 005 0V12a9 9 0 10-9 9m4.5-1.206a8.959 8.959 0 01-4.5 1.207" />
                      </svg>
                      <input
                        type="email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        className="input-glass w-full pl-10 pr-4"
                        placeholder="farmacia@email.com"
                        required
                        disabled={isLoading}
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm sf-pro-text font-semibold text-gray-700 mb-2">Senha</label>
                    <div className="relative">
                      <svg className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                      </svg>
                      <input
                        type="password"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        className="input-glass w-full pl-10 pr-4"
                        placeholder="Sua senha"
                        required
                        disabled={isLoading}
                      />
                    </div>
                  </div>

                  <button
                    type="submit"
                    disabled={isLoading}
                    className="button-primary bg-gradient-to-r from-emerald-500 to-teal-500 hover:from-emerald-600 hover:to-teal-600 text-white w-full"
                  >
                    {isLoading ? (
                      <div className="flex items-center justify-center space-x-3">
                        <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                        <span className="sf-pro-text font-semibold">Entrando...</span>
                      </div>
                    ) : (
                      <div className="flex items-center justify-center space-x-2">
                        <span className="sf-pro-text font-semibold">Entrar na Scriptus Pharma</span>
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 16l-4-4m0 0l4-4m-4 4h14m-5 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h7a3 3 0 013 3v1" />
                        </svg>
                      </div>
                    )}
                  </button>

                  <div className="text-center">
                    <button
                      type="button"
                      onClick={handleEsqueciSenha}
                      className="text-gray-600 hover:text-emerald-600 sf-pro-text font-medium hover:underline transition text-sm"
                      disabled={isLoading}
                    >
                      Esqueci minha senha
                    </button>
                  </div>

                  <div className="text-center pt-4 border-t border-gray-200/50">
                    <p className="text-sm sf-pro-text text-gray-600">
                      Não tem conta?{' '}
                      <Link to="/register" className="text-emerald-600 hover:text-emerald-700 sf-pro-text font-semibold hover:underline transition">
                        Cadastre-se grátis
                      </Link>
                    </p>
                  </div>
                </form>
              </div>

              {/* Elementos decorativos */}
              <div className="absolute -top-4 -right-4 w-24 h-24 bg-gradient-to-br from-emerald-400 to-teal-500 rounded-full opacity-10"></div>
              <div className="absolute -bottom-4 -left-4 w-32 h-32 bg-gradient-to-br from-teal-400 to-emerald-500 rounded-full opacity-10"></div>
            </div>
          </div>
        </div>
      </div>

      {/* Seção de Segurança */}
      <section className="py-16 px-6 bg-white/50 backdrop-blur-sm border-t border-gray-200/50 relative z-10">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-2xl sf-pro-display font-semibold text-gray-900 mb-4 tracking-tight">
              Compliance e <span className="text-emerald-600">Segurança</span> Garantidos
            </h2>
            <p className="text-gray-600 sf-pro-text font-medium">Tecnologias de ponta para proteger sua farmácia e seus pacientes</p>
          </div>
          
          <div className="grid md:grid-cols-3 gap-6">
            <div className="glass-card rounded-2xl p-6 text-center shadow-sm">
              <div className="icon-container w-12 h-12 bg-emerald-100/80 border-emerald-200/50 rounded-xl flex items-center justify-center mx-auto mb-4">
                <svg className="w-6 h-6 text-emerald-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                </svg>
              </div>
              <h3 className="sf-pro-text font-semibold text-gray-800 mb-2">Validação ICP-Brasil</h3>
              <p className="text-sm sf-pro-text text-gray-600">Receitas verificadas com certificado digital</p>
            </div>

            <div className="glass-card rounded-2xl p-6 text-center shadow-sm">
              <div className="icon-container w-12 h-12 bg-teal-100/80 border-teal-200/50 rounded-xl flex items-center justify-center mx-auto mb-4">
                <svg className="w-6 h-6 text-teal-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
              </div>
              <h3 className="sf-pro-text font-semibold text-gray-800 mb-2">SNGPC Automático</h3>
              <p className="text-sm sf-pro-text text-gray-600">XML gerado conforme padrão ANVISA</p>
            </div>

            <div className="glass-card rounded-2xl p-6 text-center shadow-sm">
              <div className="icon-container w-12 h-12 bg-blue-100/80 border-blue-200/50 rounded-xl flex items-center justify-center mx-auto mb-4">
                <svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                </svg>
              </div>
              <h3 className="sf-pro-text font-semibold text-gray-800 mb-2">LGPD Compliant</h3>
              <p className="text-sm sf-pro-text text-gray-600">Dados protegidos e criptografados</p>
            </div>
          </div>
        </div>
      </section>

      {/* Estilos CSS */}
      <style>{`
        .sf-pro-display {
          font-family: -apple-system, "SF Pro Display", BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", sans-serif;
          font-feature-settings: "liga" 1, "kern" 1;
          -webkit-font-smoothing: antialiased;
        }
        
        .sf-pro-text {
          font-family: -apple-system, "SF Pro Text", BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", sans-serif;
          font-feature-settings: "liga" 1, "kern" 1;
          -webkit-font-smoothing: antialiased;
        }

        .glass-card {
          background: rgba(255, 255, 255, 0.8);
          backdrop-filter: blur(10px);
          -webkit-backdrop-filter: blur(10px);
          border: 1px solid rgba(255, 255, 255, 0.25);
          box-shadow: 0 4px 16px rgba(0, 0, 0, 0.08);
        }

        .glass-card-elevated {
          background: rgba(255, 255, 255, 0.85);
          backdrop-filter: blur(12px);
          -webkit-backdrop-filter: blur(12px);
          border: 1px solid rgba(255, 255, 255, 0.3);
          box-shadow: 0 8px 24px rgba(0, 0, 0, 0.1);
        }

        .icon-container {
          background: rgba(255, 255, 255, 0.5);
          backdrop-filter: blur(6px);
          -webkit-backdrop-filter: blur(6px);
          border: 1px solid rgba(255, 255, 255, 0.25);
          box-shadow: 0 2px 4px rgba(0, 0, 0, 0.04);
        }

        .icon-container-pharma {
          background: rgba(255, 255, 255, 0.9);
          backdrop-filter: blur(12px);
          -webkit-backdrop-filter: blur(12px);
          border: 1px solid rgba(255, 255, 255, 0.4);
          box-shadow: 0 4px 16px rgba(16, 185, 129, 0.15), inset 0 1px 0 rgba(255, 255, 255, 0.8);
        }

        .glass-header {
          background: rgba(255, 255, 255, 0.4);
          backdrop-filter: blur(8px);
          -webkit-backdrop-filter: blur(8px);
          border-bottom: 1px solid rgba(255, 255, 255, 0.15);
          box-shadow: 0 1px 2px rgba(0, 0, 0, 0.04);
        }

        .glass-header-scrolled {
          background: rgba(255, 255, 255, 0.6);
          backdrop-filter: blur(12px);
          -webkit-backdrop-filter: blur(12px);
          border-bottom: 1px solid rgba(255, 255, 255, 0.3);
          box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
        }

        .input-glass {
          padding: 12px 16px;
          background: rgba(255, 255, 255, 0.7);
          border: 1px solid rgba(255, 255, 255, 0.3);
          border-radius: 12px;
          backdrop-filter: blur(8px);
          -webkit-backdrop-filter: blur(8px);
          transition: all 0.2s ease;
          font-family: -apple-system, "SF Pro Text", BlinkMacSystemFont, sans-serif;
          font-size: 14px;
          color: #1f2937;
          box-shadow: 0 2px 4px rgba(0, 0, 0, 0.04);
        }

        .input-glass::placeholder { color: #9ca3af; }

        .input-glass:focus {
          outline: none;
          background: rgba(255, 255, 255, 0.9);
          border-color: rgba(16, 185, 129, 0.5);
          box-shadow: 0 0 0 3px rgba(16, 185, 129, 0.1), 0 4px 12px rgba(0, 0, 0, 0.08);
          transform: translateY(-1px);
        }

        .input-glass:disabled {
          opacity: 0.6;
          cursor: not-allowed;
        }

        .button-glass {
          padding: 8px 14px;
          background: rgba(255, 255, 255, 0.7);
          backdrop-filter: blur(8px);
          -webkit-backdrop-filter: blur(8px);
          border: 1px solid rgba(255, 255, 255, 0.3);
          border-radius: 10px;
          font-family: -apple-system, "SF Pro Text", BlinkMacSystemFont, sans-serif;
          font-weight: 500;
          transition: all 0.2s ease;
          box-shadow: 0 2px 4px rgba(0, 0, 0, 0.04);
        }

        .button-glass:hover:not(:disabled) {
          background: rgba(255, 255, 255, 0.9);
          transform: translateY(-1px);
          box-shadow: 0 4px 8px rgba(0, 0, 0, 0.08);
        }

        .button-primary {
          padding: 14px 20px;
          border-radius: 12px;
          font-family: -apple-system, "SF Pro Text", BlinkMacSystemFont, sans-serif;
          font-weight: 600;
          transition: all 0.2s ease;
          font-size: 16px;
          box-shadow: 0 4px 12px rgba(16, 185, 129, 0.3);
        }

        .button-primary:hover:not(:disabled) {
          transform: translateY(-2px);
          box-shadow: 0 6px 20px rgba(16, 185, 129, 0.4);
        }

        .button-primary:disabled {
          opacity: 0.6;
          cursor: not-allowed;
          transform: none;
        }

        .glass-badge {
          background: rgba(243, 244, 246, 0.8);
          backdrop-filter: blur(8px);
          -webkit-backdrop-filter: blur(8px);
          color: #6b7280;
          border-radius: 10px;
          border: 1px solid rgba(255, 255, 255, 0.3);
          box-shadow: 0 2px 4px rgba(0, 0, 0, 0.04);
        }

        .glass-alert {
          padding: 14px 16px;
          border-radius: 12px;
          backdrop-filter: blur(8px);
          -webkit-backdrop-filter: blur(8px);
          box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
        }

        @media (max-width: 768px) {
          .glass-card-elevated, .glass-card, .glass-header, .glass-header-scrolled {
            backdrop-filter: blur(8px);
            -webkit-backdrop-filter: blur(8px);
          }
          .input-glass { font-size: 16px; }
        }

        @media (prefers-reduced-motion: reduce) {
          * {
            animation-duration: 0.01ms !important;
            transition-duration: 0.01ms !important;
          }
        }
      `}</style>
    </div>
  );
}

export default Login;